import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GalleryImage {
  id: string;
  url: string;
  alt_text?: string;
  review_id: string;
  rating: number;
  excerpt?: string;
  first_name?: string;
}

interface ReviewGalleryProps {
  images: GalleryImage[];
  className?: string;
}

export default function ReviewGallery({ images, className }: ReviewGalleryProps) {
  const { t } = useTranslation('product');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (images.length === 0) return null;

  return (
    <>
      <div className={cn('', className)}>
        <h3 className="text-sm font-medium text-ink-soft mb-3">{t('reviewGallery.heading')}</h3>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {images.map((img, i) => (
            <button
              key={img.id}
              onClick={() => setSelectedIndex(i)}
              className="shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-line hover:border-sage-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
            >
              <img
                src={img.url}
                alt={img.alt_text || t('reviewGallery.thumbnailAlt', { index: i + 1 })}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedIndex(null)}
          role="dialog"
          aria-label={t('reviewGallery.lightboxAriaLabel')}
        >
          <div
            className="relative max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute -top-10 right-0 text-white hover:text-cta-fg/70" /* dark-exempt: controls on a fixed-dark lightbox */
              aria-label={t('reviewGallery.close')}
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src={images[selectedIndex].url}
              alt={images[selectedIndex].alt_text || t('reviewGallery.photoAlt')}
              className="w-full rounded-lg"
            />
            {images[selectedIndex].excerpt && (
              <div className="mt-3 bg-white/10 backdrop-blur rounded-lg p-3">
                <div className="flex items-center gap-1 mb-1">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Star
                      key={i}
                      className={cn(
                        'h-3.5 w-3.5',
                        i < images[selectedIndex].rating
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-cta-fg/40' /* dark-exempt: empty star on a fixed-dark lightbox */
                      )}
                    />
                  ))}
                </div>
                <p className="text-sm text-white/80">{images[selectedIndex].excerpt}</p>
                {images[selectedIndex].first_name && (
                  <p className="text-xs text-white/50 mt-1">
                    {t('reviewGallery.byAuthor', { name: images[selectedIndex].first_name })}
                  </p>
                )}
              </div>
            )}
            {/* Nav arrows */}
            <div className="absolute inset-y-0 left-0 flex items-center">
              {selectedIndex > 0 && (
                <button
                  onClick={() => setSelectedIndex(selectedIndex - 1)}
                  className="ml-2 bg-black/50 text-white rounded-full p-2 hover:bg-black/70"
                  aria-label={t('reviewGallery.previous')}
                >
                  &larr;
                </button>
              )}
            </div>
            <div className="absolute inset-y-0 right-0 flex items-center">
              {selectedIndex < images.length - 1 && (
                <button
                  onClick={() => setSelectedIndex(selectedIndex + 1)}
                  className="mr-2 bg-black/50 text-white rounded-full p-2 hover:bg-black/70"
                  aria-label={t('reviewGallery.next')}
                >
                  &rarr;
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
